import { bind, classNames, Component, effect, h, signal, tag } from "omi";

import { ImageZoomModal, type ImageZoomModalData } from "@/components/modals/modalTypes/imageZoomModal";
import { LoginModal, type LoginModalData } from "@/components/modals/modalTypes/loginModal";
import { MobileModal } from "@/components/modals/modalTypes/mobileModal";
import { PaywallModal, type PaywallModalData } from "@/components/modals/modalTypes/paywallModal";
import {
  PortfolioDeleteModal,
  PortfolioEditModal,
  PortfolioUploadModal,
  type PortfolioDeleteModalData,
  type PortfolioEditModalData,
  type PortfolioUploadModalData,
} from "@/components/modals/modalTypes/portfolio";
import { ProjectUpdateModal, type ProjectUpdateModalProps } from "@/components/modals/modalTypes/projectUpdateModal";
import { ShareModal, type ShareModalData } from "@/components/modals/modalTypes/shareModal";
import { WelcomeToProModal, type WelcomeToProModalData } from "@/components/modals/modalTypes/welcomeToProModal";
import { ProjectResetModal, type ProjectResetModalData } from "@/pages/project/ui/modals/resetProject";
import { animateCss } from "@/globals/styles";
import { onEscPress } from "@/utils/keys";
import { ModalId } from "@/components";
import { tailwind } from "@/tailwind";
import { isMobileDevice } from "@/utils";

import { ModalEvents, ModalOpenEvent } from "./events";

type Modal = {
  type: ModalId;
  element: JSX.Element;
};

@tag("nw-modal-provider")
export class NWModalProvider extends Component {
  static css = [tailwind, animateCss];

  private activeModal = signal<Modal | undefined>(undefined);

  install() {
    addEventListener(ModalEvents.OPEN, this.showModal as EventListener);
    addEventListener(ModalEvents.CLOSE, this.closeModal);
  }

  installed() {
    let removeListener: () => void;

    effect(() => {
      if (this.activeModal.value && !isMobileDevice()) {
        removeListener = onEscPress(this.closeModal);
      } else {
        removeListener && removeListener();
      }
    });
  }

  @bind
  showModal(event: CustomEvent<ModalOpenEvent>) {
    const element = this.getModalFromType(event);
    if (element) {
      if (this.activeModal.value != null && this.activeModal.value.type !== event.detail.modalId) {
        this.closeModal();
        setTimeout(() => this.showModal(event), 200);
      } else {
        this.activeModal.value = { element, type: event.detail.modalId as ModalId };
      }
    }
  }

  private getModalFromType(event: CustomEvent<ModalOpenEvent>): JSX.Element | undefined {
    const { modalId, data } = event.detail;

    switch (modalId as ModalId) {
      case "share":
        return <ShareModal close={this.closeModal} data={data as ShareModalData} />;
      case "paywall":
        return <PaywallModal close={this.closeModal} data={data as PaywallModalData} />;
      case "login":
        return <LoginModal close={this.closeModal} data={data as LoginModalData} />;
      case "imageZoom":
        return <ImageZoomModal close={this.closeModal} data={data as ImageZoomModalData} />;
      case "welcomeToPro":
        return <WelcomeToProModal close={this.closeModal} data={data as WelcomeToProModalData} />;
      case "mobileLock":
        return <MobileModal close={this.closeModal} data={data as WelcomeToProModalData} />;
      case "portfolioUpload":
        return <PortfolioUploadModal close={this.closeModal} data={data as PortfolioUploadModalData} />;
      case "portfolioEdit":
        return <PortfolioEditModal close={this.closeModal} data={data as PortfolioEditModalData} />;
      case "portfolioDelete":
        return <PortfolioDeleteModal close={this.closeModal} data={data as PortfolioDeleteModalData} />;
      case "projectUpdate":
        return <ProjectUpdateModal close={this.closeModal} data={data as ProjectUpdateModalProps} />;
      case "projectReset":
        return <ProjectResetModal close={this.closeModal} data={data as ProjectResetModalData} />;
      default:
        return undefined;
    }
  }

  @bind
  private closeModal() {
    const modalBg = this.shadowRoot?.getElementById("modal-bg");
    if (!modalBg) {
      return;
    }

    modalBg?.classList.remove("animate__fadeIn");
    modalBg?.classList.add("animate__fadeOut");

    setTimeout(() => {
      this.activeModal.value = undefined;

      modalBg?.classList.remove("animate__fadeOut");
      modalBg?.classList.add("animate__fadeIn");
    }, 250);
  }

  @bind
  private handleBackgroundOnClick() {
    if (this.activeModal.value?.type === "projectUpdate") return;

    this.closeModal();
  }

  render() {
    if (!this.activeModal.value) {
      return <div></div>;
    }

    return (
      <div
        id="modal-bg"
        style={"--animate-duration: 0.25s"}
        class={classNames(
          {
            hidden: !this.activeModal.value,
            "pointer-events-none sm:pointer-events-auto":
              this.activeModal.value?.type === "login" ||
              this.activeModal.value?.type === "mobileLock" ||
              this.activeModal.value?.type === "projectUpdate",
          },
          "z-[800] animate__animated animate__fadeIn fixed top-0 left-0 right-0 bottom-0 overflow-y-auto backdrop-blur-sm flex items-center justify-center bg-[#0000001A]",
        )}
        onClick={this.handleBackgroundOnClick}
      >
        <div
          class={classNames("modal-container px-4 sm:px-0 animate__animated animate__zoomIn py-4 sm:py-0", {
            "w-[98%] sm:w-[90%] xl:w-3/4 max-w-screen-xl min-w-[340px] pointer-events-auto":
              this.activeModal.value?.type === "login",
          })}
          onClick={e => e.stopPropagation()}
        >
          {this.activeModal.value?.element}
        </div>
      </div>
    );
  }
}
